/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — patterns.ts
   Shared regex patterns for account detection and classification.
   Single source of truth — all modules import from here.
   ═══════════════════════════════════════════════════════════════════════ */

// ─── Account Type Detection ─────────────────────────────────────────────
export const CASH_PATTERN = /^cash$|^cash\s|^bank|^checking|^savings|cash\s*equiv|money\s*market|petty\s*cash/i;
export const DEBT_PATTERN = /loan|notes?\s*payable|note.?payable|mortgage|line.?of.?credit|debt|borrowing|bond/i;
export const DEPRECIATION_PATTERN = /depreciation/i;
export const AMORTIZATION_PATTERN = /amortization/i;
export const ACCUMULATED_DEPRECIATION_PATTERN = /accumulated\s*depreciation|accum.*dep/i;
export const INVENTORY_PATTERN = /inventor/i;
export const AR_PATTERN = /accounts?\s*receivable|trade\s*receivable|a[\s/]*r\b/i;
export const AP_PATTERN = /accounts?\s*payable|trade\s*payable|a[\s/]*p\b/i;
export const INTANGIBLE_PATTERN = /intangible|goodwill|patent|trademark|copyright|customer\s*(list|relationship)|franchise|software|license/i;
export const LEASE_PATTERN = /lease|right.of.use|rou\b/i;
export const PPE_PATTERN = /property|plant|equipment|furniture|fixture|vehicle|building|land|machinery|computer|leasehold/i;
export const EQUITY_PATTERN = /common\s*stock|preferred\s*stock|retained\s*earn|additional\s*paid|treasury/i;
export const BAD_DEBT_PATTERN = /bad\s*debt|doubtful|allowance\s*expense|allowance|provision\s*for/i;
export const IMPAIRMENT_PATTERN = /impairment/i;
export const STOCK_COMP_PATTERN = /stock.based\s*comp|share.based\s*comp/i;
export const DEFERRED_TAX_PATTERN = /deferred\s*(tax|income\s*tax)/i;
export const UNREALIZED_PATTERN = /unrealized\s*(gain|loss)/i;
export const GAIN_ON_DISPOSAL_PATTERN = /gain\s*on\s*(disposal|sale)/i;
export const LOSS_ON_DISPOSAL_PATTERN = /loss\s*on\s*(disposal|sale|write.?off|abandon)/i;

// ─── Non-Cash Item Detection (for indirect method cash flow) ────────────

export interface NoncashPatternEntry {
  pattern: RegExp;
  label: string;
  negate?: boolean;
}

export const NONCASH_PATTERNS: NoncashPatternEntry[] = [
  { pattern: DEPRECIATION_PATTERN, label: 'Depreciation' },
  { pattern: AMORTIZATION_PATTERN, label: 'Amortization' },
  {
    pattern: /bad\s*debt|doubtful|allowance\s*expense|provision\s*for/i,
    label: 'Bad Debt Expense',
  },
  {
    pattern: LOSS_ON_DISPOSAL_PATTERN,
    label: 'Loss on Disposal of Assets',
  },
  {
    pattern: GAIN_ON_DISPOSAL_PATTERN,
    label: 'Gain on Disposal of Assets',
    negate: true,
  },
  {
    pattern: STOCK_COMP_PATTERN,
    label: 'Stock-Based Compensation',
  },
  {
    pattern: DEFERRED_TAX_PATTERN,
    label: 'Deferred Income Tax',
  },
  { pattern: IMPAIRMENT_PATTERN, label: 'Impairment Loss' },
  { pattern: /unrealized\s*gain/i, label: 'Unrealized Gain', negate: true },
  { pattern: /unrealized\s*loss/i, label: 'Unrealized Loss' },
  {
    pattern: /accretion|discount\s*amort/i,
    label: 'Accretion / Discount Amortization',
  },
];
